import apiClient from './apiClient';
import {
  Vendor,
  VendorProduct,
  VendorsApiResponse,
  SingleVendorApiResponse,
  VendorMutationResponse,
  VendorProductsApiResponse,
  ProductMutationResponse,
  DeleteApiResponse
} from '../types/vendor';
 
const VENDOR_PATH = 'vendor';

/** Nest validation pipe rejects properties not listed on the DTO. */
const buildVendorCreatePayload = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    name: String(data.name ?? '').trim(),
  };

  const email = String(data.email ?? '').trim();
  if (email) payload.email = email;

  const phone = String(data.phone ?? '').trim();
  if (phone) payload.phone = phone;

  const address = String(data.address ?? '').trim();
  if (address) payload.address = address;

  const description = String(data.description ?? '').trim();
  if (description) payload.description = description;

  const logo = String(data.logo ?? '').trim();
  if (logo) payload.logo = logo;

  if (typeof data.isActive === 'boolean') {
    payload.isActive = data.isActive;
  }

  return payload;
};

const buildVendorUpdatePayload = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};

  if (data.name !== undefined && String(data.name).trim()) {
    payload.name = String(data.name).trim();
  }
  if (data.email !== undefined) {
    const email = String(data.email).trim();
    if (email) payload.email = email;
  }
  if (data.phone !== undefined) {
    const phone = String(data.phone).trim();
    if (phone) payload.phone = phone;
  }
  if (data.address !== undefined) {
    payload.address = String(data.address).trim();
  }
  if (data.description !== undefined) {
    payload.description = String(data.description).trim();
  }
  if (data.logo !== undefined) {
    const logo = String(data.logo).trim();
    if (logo) payload.logo = logo;
  }
  if (typeof data.isActive === 'boolean') {
    payload.isActive = data.isActive;
  }

  return payload;
};

const normalizeVendor = (body: unknown): Vendor => {
  const raw = body as Record<string, unknown>;
  return (raw?.vendor ?? raw?.data ?? raw) as Vendor;
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as { response?: { data?: { message?: string | string[] } } };
  const message = err.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string') return message;
  return fallback;
};

// Updated to accept 'search'
export const getVendors = async (
  page = 1, 
  limit = 10,
  search = '' // New parameter
): Promise<VendorsApiResponse> => {
  try {
    // Construct params dynamically
    const params: any = { page, limit };
    if (search) params.search = search;

    const response = await apiClient.get(`${VENDOR_PATH}/all`, { params });
    const body = response.data;
    const vendors = Array.isArray(body) ? body : (body as VendorsApiResponse).data ?? [];
    return {
      success: true,
      data: vendors,
      count: vendors.length,
    };
  } catch (error) {
    console.error('Failed to fetch vendors:', error);
    throw error;
  }
};

// ... keep the rest of the file exactly as it was ...
export const getVendorById = async (vendorId: string): Promise<SingleVendorApiResponse> => {
  try {
    const response = await apiClient.get(`${VENDOR_PATH}/${vendorId}`);
    const vendor = normalizeVendor(response.data);
    return { success: true, data: vendor };
  } catch (error) {
    console.error(`Failed to fetch vendor with ID ${vendorId}:`, error);
    throw error;
  }
};

export const createVendor = async (vendorData: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt'>): Promise<VendorMutationResponse> => {
  try {
    const response = await apiClient.post(
      `${VENDOR_PATH}/create`,
      buildVendorCreatePayload(vendorData as Record<string, unknown>)
    );
    const body = response.data as Record<string, unknown>;
    const vendor = normalizeVendor(body);
    return {
      success: true,
      message: (body?.message as string) ?? 'Vendor created successfully',
      vendor,
    };
  } catch (error) {
    console.error('Failed to create vendor:', error);
    throw error;
  }
};

export const updateVendor = async (vendorId: string, updates: Partial<Vendor>): Promise<VendorMutationResponse> => {
  try {
    const response = await apiClient.patch(
      `${VENDOR_PATH}/${vendorId}`,
      buildVendorUpdatePayload(updates as Record<string, unknown>)
    );
    const body = response.data as Record<string, unknown>;
    const vendor = normalizeVendor(body);
    return {
      success: true,
      message: (body?.message as string) ?? 'Vendor updated successfully',
      vendor,
    };
  } catch (error) {
    console.error(`Failed to update vendor ${vendorId}:`, error);
    throw error;
  }
};

export const deleteVendor = async (vendorId: string): Promise<DeleteApiResponse> => {
  try {
    const response = await apiClient.delete<DeleteApiResponse>(`${VENDOR_PATH}/${vendorId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete vendor ${vendorId}:`, error);
    throw error;
  }
};
  
export const getVendorProducts = async (vendorId: string): Promise<VendorProductsApiResponse> => {
  try { 
    const response = await apiClient.get<VendorProductsApiResponse>(`${VENDOR_PATH}/products/all`, {
      params: { vendorId } 
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch products for vendor ${vendorId}:`, error);
    throw error;
  }
};

/**
 * Naija Shop: fetch ALL vendor products across vendors (paginated, filterable).
 * GET /admins/vendor/products/all
 */
export const getAllVendorProducts = async (
  page = 1,
  limit = 10,
  filters: { search?: string; category?: string; isFeatured?: boolean } = {}
): Promise<VendorProductsApiResponse> => {
  try {
    const params: any = { page, limit };
    if (filters.search) params.search = filters.search;
    if (filters.category) params.category = filters.category;
    if (typeof filters.isFeatured === "boolean") params.isFeatured = filters.isFeatured;

    const response = await apiClient.get<VendorProductsApiResponse>(
      `${VENDOR_PATH}/products/all`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch all vendor products:", error);
    throw error;
  }
};
 
export const createVendorProduct = async (
  vendorId: string,
  productData: FormData
): Promise<ProductMutationResponse> => {
  try {
    // Let axios set multipart boundary — do NOT set Content-Type manually
    const response = await apiClient.post<ProductMutationResponse>(
      `${VENDOR_PATH}/${vendorId}/products/create`,
      productData
    );
    return response.data;
  } catch (error: any) {
    console.error(`Failed to add product to vendor ${vendorId}:`, error);
    if (error.response) console.error("Backend Error Response:", error.response.data);
    throw error;
  }
};

export const updateVendorProduct = async (productId: string, updates: Partial<VendorProduct>): Promise<ProductMutationResponse> => {
  try {
    const response = await apiClient.patch<ProductMutationResponse>(`${VENDOR_PATH}/products/${productId}`, updates);
    return response.data;
  } catch (error) {
    console.error(`Failed to update product ${productId}:`, error);
    throw error;
  }
};
 
export const updateProductPricing = async (
    productId: string,
    pricingUpdates: { priceNGN?: number; priceGBP?: number; availableInNigeria?: boolean; availableInUK?: boolean }
): Promise<ProductMutationResponse> => {
  try {
    const response = await apiClient.patch<ProductMutationResponse>(`${VENDOR_PATH}/products/${productId}/pricing`, pricingUpdates);
    return response.data;
  } catch (error) {
    console.error(`Failed to update pricing for product ${productId}:`, error);
    throw error;
  }
};
 
export const setProductFeatured = async (
  productId: string,
  isFeatured: boolean
): Promise<ProductMutationResponse> => {
  try {
    const response = await apiClient.patch<ProductMutationResponse>(
      `${VENDOR_PATH}/products/${productId}/featured`,
      { isFeatured }  
    );
    return response.data;
  } catch (error: any) {
    console.error(`Failed to update featured status for product ${productId}:`, error);
    throw error;
  }
};

export const deleteVendorProduct = async (productId: string): Promise<DeleteApiResponse> => {
    try {
        const response = await apiClient.delete<DeleteApiResponse>(`${VENDOR_PATH}/products/${productId}`);
        return response.data;
    } catch (error) {
        console.error(`Failed to delete product ${productId}:`, error);
        throw error;
    }
};